import { LitElement } from '@lit'

class NovaVariantPicker extends LitElement {
  static properties = {
    productUrl: { type: String, attribute: 'product-url' },
    sectionId: { type: String, attribute: 'section-id' },
    variantData: { type: Object },
    currentVariant: { type: Object, state: true },
  }

  connectedCallback() {
    this.abortController = undefined

    super.connectedCallback()
    this._initializeElements()
    this._addEventListeners()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._removeEventListeners()
  }

  _initializeElements() {
    this.$optionInputs = this.querySelectorAll('.js-options-input')
    this.$selectInputs = this.querySelectorAll('.js-select-input')
    this.$colorLegend = this.querySelector('.js-color-legend')
  }

  _addEventListeners() {
    for (const $input of this.$optionInputs) {
      $input.addEventListener('change', this._handleOptionChange)
    }

    for (const $input of this.$selectInputs) {
      $input.addEventListener('sl-change', this._handleOptionChange)
    }

    document.addEventListener(
      'nova:product:render-variant-picker',
      this._handleVariantPickerUpdate,
    )
  }

  _removeEventListeners() {
    for (const $input of this.$selectInputs) {
      $input.removeEventListener('sl-change', this._handleOptionChange)
    }

    document.removeEventListener(
      'nova:product:render-variant-picker',
      this._handleVariantPickerUpdate,
    )
  }

  _handleVariantPickerUpdate = (event) => {
    this.updateContent(event.detail.html)
  }

  updateURL(url) {
    window.history.replaceState(
      {},
      '',
      `${url}${this.currentVariant?.id ? `?variant=${this.currentVariant?.id}` : ''}`,
    )
  }

  _handleOptionChange = (event) => {
    const productUrl =
      event.target.dataset?.productUrl ||
      event.target.querySelector('[aria-selected="true"]').dataset
        ?.productUrl ||
      this.productUrl

    const shouldSwatpProduct = this.productUrl !== productUrl

    this.renderProductInfo({
      targetId: event.target.id,
      requestUrl: this.buildRequestUrlWithParams(
        productUrl,
        shouldSwatpProduct,
      ),
      callback: shouldSwatpProduct
        ? this.handleSwapProduct(productUrl)
        : this.handleUpdateProductInfo(productUrl),
    })
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    this.abortController?.abort()
    this.abortController = new AbortController()

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        callback(html)
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user')
        } else {
          console.error(error)
        }
      })
  }

  buildRequestUrlWithParams(productUrl, shouldSwatpProduct = false) {
    const selectedOptionValues = Array.from(
      this.querySelectorAll(
        'fieldset input:checked, fieldset sl-select sl-option[aria-selected="true"]',
      ),
    )
      .sort(
        (a, b) =>
          Number.parseInt(a.dataset.optionPosition) -
          Number.parseInt(b.dataset.optionPosition),
      )
      .map(({ dataset }) => dataset.valueId)
    const params = []

    if (!shouldSwatpProduct) {
      params.push(`section_id=${this.sectionId}`)
    }

    if (selectedOptionValues.length) {
      params.push(`option_values=${selectedOptionValues.join(',')}`)
    }

    return `${productUrl}?${params.join('&')}`
  }

  handleSwapProduct(productUrl) {
    return (html) => {
      document.querySelector('head title').innerHTML =
        html.querySelector('head title').innerHTML
      const updateSourceFromDestination = (id) => {
        const destination = document.querySelector(`#${id}-${this.sectionId}`)
        const source = html.querySelector(`#${id}-${this.sectionId}`)

        if (source && destination) {
          destination.innerHTML = source.innerHTML
        }
      }
      for (const id of ['ProductTitle', 'ProductDescription']) {
        updateSourceFromDestination(id)
      }

      this.currentVariant = JSON.parse(
        html.querySelector('[data-selected-variant]').innerHTML,
      )

      const newPicker = html.querySelector('nova-variant-picker')
      if (newPicker) {
        if (newPicker.dataset.defaultPreviewSrc) {
          this.dataset.defaultPreviewSrc = newPicker.dataset.defaultPreviewSrc
        }
        if (newPicker.dataset.defaultPreviewAlt !== undefined) {
          this.dataset.defaultPreviewAlt = newPicker.dataset.defaultPreviewAlt
        }
        if (newPicker.dataset.productTitle) {
          this.dataset.productTitle = newPicker.dataset.productTitle
        }
      }

      this.updateURL(productUrl)
      this.updateSectionContents(html)
      this.setAvailability(this.currentVariant)
      this._updateVariantPreview()
    }
  }

  handleUpdateProductInfo(productUrl) {
    return (html) => {
      this.currentVariant = JSON.parse(
        html.querySelector('[data-selected-variant]').innerHTML,
      )

      this.updateURL(productUrl)
      this.updateSectionContents(html)
      this.setAvailability(this.currentVariant)
      this._updateVariantPreview()
    }
  }

  _updateVariantPreview() {
    const root = this.closest('.variant-picker__with-preview')
    if (!root) return

    const dialogId = `VariantPreviewLightbox-${this.sectionId}`
    const previewDialog =
      root.querySelector('sl-dialog.variant-picker__preview-dialog') ||
      document.getElementById(dialogId)
    const img = root.querySelector('.js-variant-preview-img')
    const lightboxImg =
      root.querySelector('.js-variant-preview-lightbox-img') ||
      previewDialog?.querySelector('.js-variant-preview-lightbox-img')
    const trigger = root.querySelector('.js-variant-preview-open')

    if (!img && !lightboxImg && !trigger) return

    const v = this.currentVariant
    const variantSrc = v?.featured_image?.src
    const fallbackSrc = this.dataset.defaultPreviewSrc || ''
    const src = variantSrc || fallbackSrc
    const alt =
      v?.featured_image?.alt ||
      this.dataset.defaultPreviewAlt ||
      this.dataset.productTitle ||
      ''

    if (img) {
      if (src) {
        img.src = src
        img.removeAttribute('hidden')
      } else {
        img.setAttribute('hidden', '')
      }
      img.alt = alt
    }

    if (lightboxImg) {
      if (src) {
        lightboxImg.src = src
        lightboxImg.removeAttribute('hidden')
      } else {
        lightboxImg.setAttribute('hidden', '')
      }
      lightboxImg.alt = alt
    }

    if (trigger) {
      if (src) {
        trigger.disabled = false
        trigger.removeAttribute('aria-hidden')
      } else {
        trigger.disabled = true
        trigger.setAttribute('aria-hidden', 'true')
      }
    }

    if (previewDialog) {
      previewDialog.label = alt || this.dataset.productTitle || ''
    }
  }

  setAvailability(currentVariant) {
    const selectors = ['price', 'Inventory']
      .map((id) => `#${id}-${this.sectionId}`)
      .join(', ')

    for (const element of document.querySelectorAll(selectors)) {
      element.classList[currentVariant ? 'remove' : 'add']('hidden')
    }
    const buyButton = document.querySelector(
      `#product-form-${this.sectionId} .js-product-buy-button`,
    )

    if (this.currentVariant) {
      if (this.currentVariant.available) {
        buyButton.innerHTML = window.variantStrings.addToCart
        buyButton.removeAttribute('disabled')
      } else {
        buyButton.innerHTML = window.variantStrings.soldOut
        buyButton.setAttribute('disabled', '')
      }
    } else {
      buyButton.setAttribute('disabled', '')
      buyButton.innerHTML = window.variantStrings.unavailable
    }
  }

  updateSectionContents(html) {
    const elements = [
      {
        selector: 'nova-product-gallery',
        eventName: 'nova:product:render-gallery',
      },
      {
        selector: '.js-product-lightbox',
        eventName: 'nova:product:render-gallery-lightbox',
      },
      {
        selector: 'nova-variant-picker',
        eventName: 'nova:product:render-variant-picker',
      },
      { selector: '.js-product-form', eventName: 'nova:product:render-form' },
      { selector: 'nova-price', eventName: 'nova:product:render-price' },
      {
        selector: 'nova-quantity-input',
        eventName: 'nova:product:render-quantity-input',
      },
      {
        selector: '.js-accordion-product-details',
        eventName: 'nova:product:render-accordion-product-details',
      },
    ]

    for (const { selector, eventName } of elements) {
      const element = html.querySelector(selector)

      if (element) {
        this.dispatchCustomEvent(eventName, element)
      }
    }

    for (const badge of document.querySelectorAll('[data-stock-badge]')) {
      const source = html.querySelector(
        `[data-stock-badge][data-block-id="${badge.dataset.blockId}"]`,
      )

      if (source) {
        badge.innerHTML = source.innerHTML
      }
    }
  }

  dispatchCustomEvent(eventName, htmlElement) {
    document.dispatchEvent(
      new CustomEvent(eventName, {
        detail: { html: htmlElement },
      }),
    )
  }

  updateContent(content) {
    this.innerHTML = content.innerHTML

    this.requestUpdate()

    this._initializeElements()
    this._addEventListeners()
  }

  createRenderRoot() {
    return this
  }
}

customElements.define('nova-variant-picker', NovaVariantPicker)
